import { useCallback, useState } from 'react';
import {
    bulkPushToLive,
    BulkPushItem,
    BulkPushProgress,
    BulkPushResult,
} from '../services/OnboardingIntelligenceService';
import { useMountedRef } from './useMountedRef';

interface BulkPushProgressState {
    done: number;
    total: number;
}

export interface UseBulkPushResult {
    busy: boolean;
    progress: BulkPushProgressState | null;
    failures: BulkPushResult[];
    error: string | null;
    run: (
        items: BulkPushItem[],
        options?: { onProgress?: BulkPushProgress }
    ) => Promise<void>;
    reset: () => void;
}

/**
 * Encapsulates the shared "bulk push to production" flow used by both the
 * Ready-to-Go column and Recommendation 03. Centralizes mount-guarded state
 * (`busy`, `progress`, `failures`), error capture, and `onComplete` refresh
 * invocation so the consuming components stay presentational.
 *
 * `progress` is null when the caller doesn't supply `onProgress` — consumers
 * branch on it to decide whether to render a determinate progress strip.
 */
export function useBulkPush(onComplete?: () => void): UseBulkPushResult {
    const [busy, setBusy] = useState<boolean>(false);
    const [progress, setProgress] = useState<BulkPushProgressState | null>(null);
    const [failures, setFailures] = useState<BulkPushResult[]>([]);
    const [error, setError] = useState<string | null>(null);
    const mountedRef = useMountedRef();

    const reset = useCallback(() => {
        setFailures([]);
        setError(null);
    }, []);

    const run = useCallback(
        async (
            items: BulkPushItem[],
            options?: { onProgress?: BulkPushProgress }
        ) => {
            if (items.length === 0) return;
            setBusy(true);
            setFailures([]);
            setError(null);
            const tracksProgress = Boolean(options?.onProgress);
            if (tracksProgress) {
                setProgress({ done: 0, total: items.length });
            }

            try {
                const results = await bulkPushToLive(
                    items,
                    tracksProgress
                        ? (done, total) => {
                              if (mountedRef.current) {
                                  setProgress({ done, total });
                              }
                              options?.onProgress?.(done, total);
                          }
                        : undefined
                );
                if (!mountedRef.current) return;
                setFailures(results.filter((r) => !r.ok));
                onComplete?.();
            } catch (err) {
                if (mountedRef.current) {
                    setError(
                        err instanceof Error ? err.message : 'Bulk push failed'
                    );
                }
            } finally {
                if (mountedRef.current) {
                    setBusy(false);
                    setProgress(null);
                }
            }
        },
        [mountedRef, onComplete]
    );

    return { busy, progress, failures, error, run, reset };
}
